import React, { useState, useEffect } from 'react';
import { Layers, ShieldCheck, UserCheck, Flame, ArrowUpRight } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import useReveal from '../../hooks/useReveal';
import { resolveMediaUrl } from '../admin/MediaUpload';

const DEFAULT_B2C_EXCHANGES = [
  { id: 'b2c_1', name: 'Lotus 1', rate: '16%', type: 'B2C', tags: 'Live Cricket, Casino & Sportsbook', logo: '', link: 'https://lotusdemo1.com/', popular: true },
  { id: 'b2c_2', name: 'Lotus 2', rate: '16%', type: 'B2C', tags: 'High Speed Sports Exchange', logo: '', link: 'https://lotusdemo2.com/' },
  { id: 'b2c_3', name: 'SouthWin', rate: '16%', type: 'B2C', tags: 'Premium Sportsbook & Casino', logo: '', link: 'https://southwindemo.com/' },
  { id: 'b2c_4', name: 'UltraWin 1', rate: '16%', type: 'B2C', tags: 'Instant IDs & Live Odds', logo: '', link: 'https://ultrawindemo1.com/' },
  { id: 'b2c_5', name: '99Exch', rate: '16%', type: 'B2C', tags: 'Top Indian Sports Exchange', logo: '', link: 'https://99exchdemo.com/', popular: true },
  { id: 'b2c_6', name: 'SkyExch 1', rate: '16%', type: 'B2C', tags: 'Premium Betting & Casino', logo: '', link: 'https://skyexchdemo1.com/', popular: true },
  { id: 'b2c_7', name: 'WinBuzz 1', rate: '16%', type: 'B2C', tags: 'Fastest Live Betting Platform', logo: '', link: 'https://winbuzzdemo1.com/', popular: true },
  { id: 'b2c_8', name: 'Tiger 1', rate: '16%', type: 'B2C', tags: 'Multi-market Live Exchange', logo: '', link: 'https://tigerdemo1.com/' },
  { id: 'b2c_9', name: '20Wicket', rate: '16%', type: 'B2C', tags: 'Dedicated Cricket & Sportsbook', logo: '', link: 'https://20wicketdemo.com/' },
  { id: 'b2c_10', name: 'FairPlay', rate: '16%', type: 'B2C', tags: 'World Class Live Casino & Exchange', logo: '', link: 'https://fairplaydemo.com/', popular: true },
  { id: 'b2c_11', name: '11xPlay', rate: '16%', type: 'B2C', tags: '100+ Live Casino Games & Odds', logo: '', link: 'https://11xplaydemo.com/' },
  { id: 'b2c_12', name: 'ReddyBook 1', rate: '16%', type: 'B2C', tags: 'Leading Club & Sports Book', logo: '', link: 'https://reddybookdemo1.com/', popular: true },
  { id: 'b2c_13', name: 'AllPanel', rate: '16%', type: 'B2C', tags: 'Master, Admin & User Panel', logo: '', link: 'https://allpaneldemo.com/' },
  { id: 'b2c_14', name: 'Laser 247', rate: '16%', type: 'B2C', tags: '24/7 Cricket & Table Games', logo: '', link: 'https://laserdemo.co/' },
  { id: 'b2c_15', name: 'Deal Demo', rate: '16%', type: 'B2C', tags: 'Direct Line & Coin Deals', logo: '', link: 'https://dealdemo.co/' },
  { id: 'b2c_16', name: 'Reddy South', rate: '16%', type: 'B2C', tags: 'Southern Club & High Roller Sportsbook', logo: '', link: 'https://reddysouth.com/' },
  { id: 'b2c_17', name: 'All Khiladi', rate: '16%', type: 'B2C', tags: 'The Ultimate Sports Platform', logo: '', link: 'https://allkhiladi.com/' }
];

const DEFAULT_B2B_EXCHANGES = [
  { id: 'b2b_1', name: '4Wicket', rate: '16%', type: 'B2B', tags: 'Distributor & Master Super Admin Portal', logo: '', link: 'https://www.4wicket.com/', popular: true },
  { id: 'b2b_2', name: 'RadheExch 247', rate: '16%', type: 'B2B', tags: 'Radhe 247 Agent & Master Network Panel', logo: '', link: 'https://radheexch247.com/' },
  { id: 'b2b_3', name: 'AllPanel Buzz', rate: '16%', type: 'B2B', tags: 'AllPanel Master, Agent & Client Portal', logo: '', link: 'https://allpanel.buzz/', popular: true },
  { id: 'b2b_4', name: 'TigerExch Pro', rate: '16%', type: 'B2B', tags: 'Tiger Pro Master Backend & Risk Management', logo: '', link: 'https://tigerexch.pro' }
];

export default function ExchangeListings() {
  const { data } = useSite();
  const { category, query, matches } = useFilter();
  const el = data.exchangeListings || {};
  const visible = (data.sectionVisibility || {}).exchanges !== false;

  // Filter tabs: 1. B2B, 2. B2C, 3. Popular
  const [activeTab, setActiveTab] = useState('b2b');

  useEffect(() => {
    if (category === 'b2b') setActiveTab('b2b');
    else if (category === 'b2c') setActiveTab('b2c');
    else if (category === 'popular') setActiveTab('popular');
  }, [category]);

  if (!visible || el.enabled === false) return null;

  // Admin Color Picker settings
  const accentColor = el.accentColor || '#ccc77d';
  const rateColor = el.rateColor || '#ccc77d';
  const b2bColor = el.b2bColor || '#ccc77d';
  const b2cColor = el.b2cColor || '#ccc77d';
  const popularColor = el.popularColor || '#fbbf24';

  // Extract from backend config if available
  const configuredTypes = (el.types && el.types.length > 0) ? el.types : null;
  const b2bType = configuredTypes?.find(t => t.id === 'b2b' || t.label?.toLowerCase()?.includes('b2b'));
  const b2cType = configuredTypes?.find(t => t.id === 'b2c' || t.label?.toLowerCase()?.includes('b2c'));

  const b2bList = (b2bType && b2bType.items && b2bType.items.length >= 4)
    ? b2bType.items.map(it => ({ ...it, type: 'B2B' }))
    : DEFAULT_B2B_EXCHANGES;

  const b2cList = (b2cType && b2cType.items && b2cType.items.length >= 10)
    ? b2cType.items.map(it => ({ ...it, type: 'C2C' in it ? it.type : 'B2C' }))
    : DEFAULT_B2C_EXCHANGES;

  const allList = [...b2bList, ...b2cList];
  const popularList = allList.filter(it => it.popular || ['b2b_1', 'b2b_3', 'b2c_1', 'b2c_5', 'b2c_6', 'b2c_7', 'b2c_10'].includes(it.id));

  const currentItems = activeTab === 'b2c'
    ? b2cList
    : activeTab === 'popular'
      ? popularList
      : b2bList;

  const q = (query || '').trim().toLowerCase();
  const displayItems = q
    ? allList.filter(matches)
    : currentItems;

  const title = el.title || 'EXCHANGE LISTINGS';

  return (
    <div id="exchanges" className="rounded-xl bg-zinc-950/40 border border-zinc-900 overflow-hidden my-4" data-testid="exchanges-section">
      {/* Header Bar with dynamic color picker gradient background */}
      <div
        className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-b border-zinc-900"
        style={{ background: `linear-gradient(90deg, ${accentColor}25 0%, ${accentColor}12 40%, rgba(9, 9, 11, 0.95) 100%)` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center font-black shadow-sm shrink-0"
            style={{ backgroundColor: accentColor, color: '#000000' }}
          >
            <Layers size={13} />
          </span>
          <span className="text-white font-extrabold text-sm md:text-base tracking-wide uppercase">
            {title}
          </span>
        </div>

        {/* Filters Order: 1. B2B, 2. B2C, 3. Popular — Dynamic Color Picker Integration */}
        <div className="flex items-center gap-1 bg-zinc-900/90 p-1 rounded-xl border border-zinc-800 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('b2b')}
            style={activeTab === 'b2b' ? { backgroundColor: b2bColor, color: '#000000', borderColor: b2bColor } : {}}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'b2b'
                ? 'shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <ShieldCheck size={12} className={activeTab === 'b2b' ? 'text-black' : 'text-white'} />
            <span>B2B ({b2bList.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('b2c')}
            style={activeTab === 'b2c' ? { backgroundColor: b2cColor, color: '#000000', borderColor: b2cColor } : {}}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'b2c'
                ? 'shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <UserCheck size={12} className={activeTab === 'b2c' ? 'text-black' : 'text-white'} />
            <span>B2C ({b2cList.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('popular')}
            style={activeTab === 'popular' ? { backgroundColor: popularColor, color: '#000000', borderColor: popularColor } : {}}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'popular'
                ? 'shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <Flame size={12} className={activeTab === 'popular' ? 'text-black' : 'text-amber-400'} />
            <span>Popular ({popularList.length})</span>
          </button>
        </div>
      </div>

      {/* Desktop Column Header */}
      <div className="hidden md:grid grid-cols-12 items-center px-4 py-2.5 border-b border-zinc-900 bg-zinc-950/60 text-xs font-semibold text-zinc-400">
        <div className="col-span-6">Exchange Platform</div>
        <div className="col-span-3 text-center">Share / Rate</div>
        <div className="col-span-3 text-right">Action</div>
      </div>

      {/* GET ID Style Rows */}
      <div className="divide-y divide-zinc-900">
        {displayItems.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-xs">
            No exchanges matching "{query}"
          </div>
        ) : (
          displayItems.map((m, i) => (
            <ExchangeRow
              key={m.id}
              m={m}
              delay={i * 30}
              waNumber={(data?.exchangeListings || {}).whatsapp || data?.whatsappLink || (data?.social || {}).whatsapp || ''}
              accentColor={accentColor}
              rateColor={rateColor}
              b2bColor={b2bColor}
              b2cColor={b2cColor}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ExchangeRow({ m, delay, waNumber, accentColor, rateColor, b2bColor, b2cColor }) {
  const { ref, visible } = useReveal();
  const isB2B = (m.type || '').toUpperCase() === 'B2B';
  const actionLabel = isB2B ? 'GET B2B' : 'GET B2C';
  const digits = waNumber.replace(/\D/g, '');
  const buttonBgColor = isB2B ? b2bColor : b2cColor;

  let actionHref = m.link || '#';
  if (digits) {
    actionHref = `https://wa.me/${digits}?text=${encodeURIComponent(`I want ${m.name} (${m.type})`.trim())}`;
  }

  const demoUrl = m.link || actionHref;

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`match-row grid grid-cols-12 items-center px-3 md:px-4 py-3 gap-2 transition-all duration-500 hover:bg-zinc-900/40 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
      }`}
    >
      {/* Platform Logo, Title with URL Link & Arrow, and Description */}
      <div className="col-span-12 md:col-span-6 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-md bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
          {m.logo ? (
            <img src={resolveMediaUrl(m.logo)} alt={m.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="font-extrabold text-xs" style={{ color: accentColor }}>
              {(m.name || 'E').slice(0, 4)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {/* Title as clickable URL link with arrow */}
          <div className="flex items-center gap-1.5">
            <a
              href={demoUrl}
              target={demoUrl.startsWith('http') ? '_blank' : '_self'}
              rel="noreferrer"
              className="text-white font-semibold text-sm truncate flex items-center gap-1 transition-colors group/link"
            >
              <span className="group-hover/link:underline">{m.name}</span>
              <ArrowUpRight size={13} className="shrink-0" style={{ color: accentColor }} />
            </a>
          </div>

          {/* Description/Tags */}
          <div className="text-zinc-400 text-[11px] truncate mt-0.5">
            {m.tags || m.description || 'Sports Exchange Platform'}
          </div>
        </div>

        {/* Mobile Action Button & Rate Box */}
        <div className="md:hidden flex items-center gap-1.5 shrink-0">
          <div
            className="h-8 px-2.5 rounded-md text-[11px] font-extrabold flex items-center justify-center shadow-xs"
            style={{ backgroundColor: rateColor, color: '#000000' }}
          >
            {m.rate || '16%'}
          </div>
          <a
            href={actionHref}
            target={actionHref.startsWith('http') ? '_blank' : '_self'}
            rel="noreferrer"
            style={{ backgroundColor: buttonBgColor, color: '#000000' }}
            className="h-8 px-3 rounded-md hover:brightness-110 text-[11px] font-bold flex items-center justify-center gap-1"
          >
            <span>{actionLabel}</span>
            <ArrowUpRight size={12} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Desktop Rate / Share Box — Bound to rateColor Picker */}
      <div className="hidden md:flex col-span-3 items-center justify-center">
        <div
          style={{ backgroundColor: rateColor, color: '#000000', borderColor: rateColor }}
          className="h-8 px-3 rounded-md text-xs font-extrabold flex items-center justify-center gap-1 shadow-xs border"
        >
          <span>{m.rate || '16%'}</span>
          <span className="text-[10px] opacity-80">Share</span>
        </div>
      </div>

      {/* Desktop Action Button (GET B2B / GET B2C) — Bound to b2bColor / b2cColor */}
      <div className="hidden md:flex col-span-3 items-center justify-end">
        <a
          href={actionHref}
          target={actionHref.startsWith('http') ? '_blank' : '_self'}
          rel="noreferrer"
          style={{ backgroundColor: buttonBgColor, color: '#000000' }}
          className="h-8 px-3 rounded-md hover:brightness-110 text-[11px] font-bold flex items-center justify-center gap-1 transition-all active:scale-95 shadow-xs"
        >
          <span>{actionLabel}</span>
          <ArrowUpRight size={12} strokeWidth={2.5} />
        </a>
      </div>
    </div>
  );
}
